import { useLanguageStore } from '../stores/useLanguageStore';
import englishMessagesDefault from '../i18n/en';
import vietnameseMessagesDefault from '../i18n/vi';
import { useMemo } from 'react';

// Handle both default export and named export
// TypeScript default exports are accessed via .default in JavaScript
// Ensure we get the actual object, not a module wrapper
const getMessages = (messages: any) => {
  if (!messages) return {};
  // If it's already an object with pos key, use it directly
  if (typeof messages === 'object' && 'pos' in messages) {
    return messages;
  }
  // Try .default property
  if (messages.default && typeof messages.default === 'object') {
    return messages.default;
  }
  // Fallback to the messages itself
  return messages;
};

const englishMessages = getMessages(englishMessagesDefault);
const vietnameseMessages = getMessages(vietnameseMessagesDefault);

// Debug: Log the structure to verify imports
if (typeof window !== 'undefined') {
  const logLevel = process.env.NODE_ENV === 'development' ? 'log' : 'warn';
  console[logLevel]('📦 Translation messages loaded:', {
    hasEnglish: !!englishMessages,
    hasVietnamese: !!vietnameseMessages,
    englishType: typeof englishMessages,
    englishIsObject:
      typeof englishMessages === 'object' && englishMessages !== null,
    englishKeys:
      englishMessages && typeof englishMessages === 'object'
        ? Object.keys(englishMessages).slice(0, 10)
        : [],
    hasPos: englishMessages?.pos ? '✅' : '❌',
    hasDashboard: englishMessages?.pos?.dashboard ? '✅' : '❌',
    hasSettings: englishMessages?.pos?.settings ? '✅' : '❌',
    hasSensitiveData: englishMessages?.pos?.settings?.sensitiveData
      ? '✅'
      : '❌',
    samplePosKey:
      englishMessages?.pos?.settings?.sensitiveData?.title || 'NOT FOUND',
  });

  // Log a sample translation to verify it works
  if (englishMessages && typeof englishMessages === 'object') {
    const testKey = 'pos.settings.sensitiveData.title';
    const keys = testKey.split('.');
    let value = englishMessages;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        value = null;
        break;
      }
    }
    console[logLevel]('🧪 Test translation lookup:', {
      key: testKey,
      found: value !== null,
      value: typeof value === 'string' ? value : 'NOT A STRING',
      valueType: typeof value,
    });
  }
}

// Simple polyglot-like implementation
class SimplePolyglot {
  private phrases: any;
  private locale: string;

  constructor(options: { locale: string; phrases: any }) {
    this.locale = options.locale;
    this.phrases = options.phrases;
  }

  t(key: string, options?: any): string {
    const keys = key.split('.');
    let value = this.phrases;

    // Debug: Only log for specific keys in development mode
    if (
      process.env.NODE_ENV === 'development' &&
      (key.includes('pos.settings.sensitiveData') ||
        key.includes('pos.previewMode'))
    ) {
      console.log('🔍 Translating key:', key);
      console.log(
        '🔍 Phrases available:',
        !!this.phrases,
        'Type:',
        typeof this.phrases
      );
      if (this.phrases?.pos?.settings) {
        console.log(
          '🔍 Has pos.settings:',
          Object.keys(this.phrases.pos.settings).slice(0, 5)
        );
      }
    }

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Only log warnings in development and for specific keys to reduce noise
        if (
          process.env.NODE_ENV === 'development' &&
          (key.includes('pos.') || key.includes('pos.common.'))
        ) {
          console.warn(
            `❌ Translation missing for key: ${key} (failed at: ${k})`
          );
          if (value && typeof value === 'object' && value !== null) {
            const availableKeys = Object.keys(value).slice(0, 20);
            console.warn(
              `   Available keys at current level:`,
              availableKeys.join(', ')
            );
            // If we're looking for 'settings' and it's not found, check if there's a 'dashboard' with settings
            if (
              k === 'settings' &&
              'dashboard' in value &&
              typeof value.dashboard === 'object'
            ) {
              console.warn(
                `   Note: Found 'dashboard.settings' but looking for 'settings' directly under 'pos'`
              );
            }
          } else if (value === null) {
            console.warn(`   Value is null`);
          } else {
            console.warn(`   Value type: ${typeof value}, value:`, value);
          }
        }
        return key;
      }
    }

    if (typeof value !== 'string') {
      // Only log warnings in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `❌ Translation value is not a string for key: ${key} (type: ${typeof value})`
        );
      }
      return key;
    }

    // Handle react-admin pluralization format (||||)
    if (value.includes('||||')) {
      const parts = value.split('||||');
      // Support both smart_count and count for pluralization
      const count = options?.smart_count ?? options?.count;
      if (count !== undefined) {
        // Use plural form (index 1) if count is not 1, otherwise use singular (index 0)
        value = count === 1 ? parts[0].trim() : parts[1].trim();
      } else {
        // Default to singular form
        value = parts[0].trim();
      }
    }

    // Handle interpolation with %{key} format
    if (options) {
      value = value.replace(/%\{(\w+)\}/g, (match: string, key: string) => {
        return options[key] !== undefined ? String(options[key]) : match;
      });
      // Also handle {key} format (without %)
      value = value.replace(/\{(\w+)\}/g, (match: string, key: string) => {
        return options[key] !== undefined ? String(options[key]) : match;
      });
    }

    return value;
  }
}

const messages: Record<string, any> = {
  en: englishMessages,
  vi: vietnameseMessages,
};

export const useTranslation = () => {
  const { locale } = useLanguageStore();

  const polyglot = useMemo(() => {
    const selectedMessages =
      messages[locale] || messages['en'] || englishMessages;

    // Debug: Log locale and messages availability
    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.log('🌐 useTranslation initialized:', {
        locale,
        hasMessagesForLocale: !!messages[locale],
        selectedMessagesType: typeof selectedMessages,
        selectedMessagesIsObject:
          typeof selectedMessages === 'object' && selectedMessages !== null,
        hasPos: selectedMessages?.pos ? '✅' : '❌',
      });
    }

    return new SimplePolyglot({
      locale: locale || 'en',
      phrases: selectedMessages || {},
    });
  }, [locale]);

  const translate = useMemo(() => {
    return (key: string, fallbackOrOptions?: string | any, options?: any) => {
      // If second parameter is a string, treat it as fallback (for backward compatibility)
      // Third parameter can be options for interpolation
      let translated: string;
      let interpolationOptions: any = null;

      if (typeof fallbackOrOptions === 'string') {
        // Fallback string provided - use it if translation is missing
        // First try to get translation without options to check if it exists
        const translationExists = polyglot.t(key) !== key;

        if (translationExists && options) {
          // Translation exists and options provided - use polyglot.t with options for interpolation
          translated = polyglot.t(key, options);
        } else if (translationExists) {
          // Translation exists but no options - get translation and apply interpolation later
          translated = polyglot.t(key);
        } else {
          // Translation doesn't exist - use fallback
          translated = fallbackOrOptions;
        }

        // If translation returned the key itself (meaning not found), use fallback
        if (translated === key) {
          translated = fallbackOrOptions;
        }

        interpolationOptions = options;
      } else {
        // Options object provided - use for interpolation
        translated = polyglot.t(key, fallbackOrOptions);
        interpolationOptions = fallbackOrOptions;
      }

      // Handle interpolation with {{}} format (for compatibility)
      if (interpolationOptions && typeof translated === 'string') {
        translated = translated.replace(
          /\{\{(\w+)\}\}/g,
          (match: string, key: string) => {
            return interpolationOptions[key] !== undefined
              ? String(interpolationOptions[key])
              : match;
          }
        );
        // Also handle {key} format (without double braces) - but only if not already replaced by polyglot
        translated = translated.replace(
          /\{(\w+)\}/g,
          (match: string, key: string) => {
            return interpolationOptions[key] !== undefined
              ? String(interpolationOptions[key])
              : match;
          }
        );
      }

      return translated;
    };
  }, [polyglot]);

  return { translate, locale };
};
